import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, LifeBuoy, LogOut, Bell, Phone, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function DashboardShell({ roleLabel, items, active, onSelect, topLabel, children, notifCount = 0 }) {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { try { await (logout && logout()); } catch (e) {} nav("/"); };
  const pick = (k) => { onSelect(k); setOpen(false); };

  const Brand = () => (
    <button onClick={() => nav("/")} className="k-tap flex items-center gap-2 border-0 bg-transparent">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center font-head font-black text-[18px]" style={{ background: "var(--k-gold)", color: "var(--k-ink)" }}>ك</div>
      <div className="text-right leading-tight">
        <div className="font-head font-black text-[17px] text-white">كزيوني</div>
        <div className="text-[9px]" style={{ color: "#C7B08A" }}>من تاجر لتاجر</div>
      </div>
    </button>
  );

  const NavList = () => (
    <nav className="flex flex-col gap-1 px-3 flex-1 overflow-y-auto no-scrollbar py-2">
      {items.map((it) => {
        const Icon = it.icon;
        const isActive = active === it.key;
        return (
          <button key={it.key} data-testid={`nav-${it.key}`} onClick={() => pick(it.key)}
            className="k-tap flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-0 text-[13px] transition-all"
            style={{ background: isActive ? "var(--k-gold)" : "transparent", color: isActive ? "var(--k-ink)" : "#C7B08A", fontWeight: isActive ? 800 : 600 }}>
            <Icon size={17} strokeWidth={isActive ? 2.6 : 2} />
            <span className="flex-1 text-right">{it.label}</span>
            {it.badge > 0 && <span className="font-num text-[9.5px] font-bold text-white flex items-center justify-center px-1.5 rounded-full" style={{ minWidth: 18, height: 18, background: "var(--k-ruby)" }}>{it.badge}</span>}
          </button>
        );
      })}
    </nav>
  );

  const SidebarInner = () => (
    <>
      <div className="px-4 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(255,255,255,.08)" }}>
        <Brand />
        <button className="md:hidden k-tap text-white/70 bg-transparent border-0" onClick={() => setOpen(false)}><X size={20} /></button>
      </div>
      <div className="px-4 py-3">
        <div className="rounded-xl px-3 py-2 text-[12px] font-bold text-white" style={{ background: "rgba(255,255,255,.06)" }}>{roleLabel}</div>
      </div>
      <NavList />
      <div className="px-3 py-3 mt-auto" style={{ borderTop: "1px solid rgba(255,255,255,.08)" }}>
        <div className="rounded-xl p-3 mb-2" style={{ background: "rgba(255,255,255,.06)" }}>
          <div className="flex items-center gap-1.5 mb-1.5"><LifeBuoy size={15} style={{ color: "var(--k-gold)" }} /><span className="text-[11.5px] text-white/80 font-bold">اتصل بنا</span></div>
          <a href="tel:0913259296" className="k-tap flex items-center gap-1.5 text-[11px] text-white/70 py-0.5"><Phone size={12} style={{ color: "var(--k-gold)" }} /><span className="font-num">0913259296</span><span className="text-[9.5px] text-white/45">· موحّد</span></a>
          <div className="my-1" style={{ borderTop: "1px solid rgba(255,255,255,.08)" }} />
          <a href="mailto:info@kazuoni.com" className="k-tap flex items-center gap-1.5 text-[11px] text-white/70 py-0.5"><Mail size={12} style={{ color: "var(--k-gold)" }} /> info@kazuoni.com</a>
          <a href="mailto:sales@kazuoni.com" className="k-tap flex items-center gap-1.5 text-[11px] text-white/70 py-0.5"><Mail size={12} style={{ color: "var(--k-gold)" }} /> sales@kazuoni.com</a>
        </div>
        <button data-testid="dash-logout" onClick={doLogout} className="k-tap w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-bold text-white/80 bg-transparent border-0"><LogOut size={14} /> تسجيل الخروج</button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen" dir="rtl">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-60 shrink-0 sticky top-0 self-start h-screen" style={{ background: "var(--k-ink)" }}>
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex" onClick={() => setOpen(false)}>
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,.5)" }} />
          <aside className="relative flex flex-col w-64 h-full k-rise" style={{ background: "var(--k-ink)" }} onClick={(e) => e.stopPropagation()}>
            <SidebarInner />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 px-4 py-2.5" style={{ background: "var(--k-paper)", borderBottom: "1px solid var(--k-line)" }}>
          <button className="md:hidden k-tap w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }} onClick={() => setOpen(true)} data-testid="dash-menu"><Menu size={18} /></button>
          <span className="font-bold text-[13px] flex-1 truncate">{topLabel}</span>
          <button onClick={() => nav("/notifications")} className="k-tap relative w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
            <Bell size={17} />
            {notifCount > 0 && <span className="font-num absolute -top-1 -right-1 text-[9px] font-bold text-white flex items-center justify-center rounded-full" style={{ minWidth: 15, height: 15, background: "var(--k-ruby)" }}>{notifCount}</span>}
          </button>
          <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-[13px]" style={{ background: "var(--k-gold-tint)", color: "var(--k-gold-deep)" }}>{(user?.name || "؟").slice(0, 1)}</div>
        </header>
        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
}
